import app from "@/main";
import {listeCevabi, temizParametreler} from "@/services/apiYardimcilari";

/**
 * Sorgu snapshot fark motorunun ürettiği haciz önerilerinin ve olay günlüğünün
 * okuma/durum uçları (Faz 2, DEV-2951 backend / DEV-2952 istemci).
 *
 * Uçlar kiracı (tenant) veritabanına bakar; `Authorization` / `Tenant-Id` /
 * `TCKN` başlıklarını `plugins/axios.ts` request interceptor'ı otomatik ekler,
 * burada elle başlık yazılmaz. Aynı interceptor'ın yanıt tarafı `response.data`
 * döndürdüğü için `$http.get(...)` doğrudan gövdeye çözülür.
 */

/** `haciz_oneri.durum` sütununun alabileceği değerler. */
export type SorguOneriDurum = "yeni" | "goruldu" | "gorev_olusturuldu" | "yok_sayildi";

/** `PATCH /sorgu-oneri/{id}` ucunun kabul ettiği geçişler. */
export type SorguOneriGecis = "goruldu" | "gorev_olusturuldu" | "yok_sayildi";

/** `sorgu_olay.tip` sütununun alabileceği değerler. */
export type SorguOlayTipi = "eklendi" | "cikarildi" | "degisti";

/**
 * Snapshot normalize katmanının ürettiği varlık gövdesi (`EntitySet` öğesi).
 * `attrs` alan bazlı değişir; maskeli/allowlist veridir, ham UYAP cevabı değil.
 */
export interface SorguOneriEntity {
    key: string;
    label: string;
    attrs: Record<string, any>;
}

/**
 * Kural motorunun istemciye bıraktığı sihirbaz köprüsü paketi.
 *
 * `takip_id` YOKTUR ve olmayacaktır: anahtar UYAP dosya kimliğidir
 * (`dosya_ref = "<birimId>:<dosyaEsasNo>"`). Toplu Haciz sihirbazı da icrapro
 * takip kimliğini hiç kullanmaz, dolayısıyla icrapro takibine eşleşmemiş
 * dosyalarda da köprü çalışır.
 */
export interface SihirbazParam {
    dosya_ref: string;
    borclu_ref: string;
    kisiKurumId: number | null;
    alan: string;
    kural: string;
    entity_key: string | null;
    entity: SorguOneriEntity | null;
}

export interface SorguOneriRow {
    id: number;
    dosya_ref: string;
    borclu_ref: string;
    kural: string;
    entity_key: string | null;
    baslik: string;
    aciklama: string | null;
    oncelik: "yuksek" | "normal";
    durum: SorguOneriDurum;
    olay_idler: number[] | null;
    sihirbaz_param: SihirbazParam | null;
    created_at: string;
    updated_at: string;
}

export interface SorguOlayRow {
    id: number;
    dosya_ref: string;
    borclu_ref: string;
    alan: string;
    tip: SorguOlayTipi;
    entity_key: string | null;
    /**
     * DİZİdir, tekil nesne değil: aynı `key` bir sürümde birden çok kez geçebiliyor
     * (takbis hisseleri), fark motoru key başına gruplayıp grubu çok-küme olarak
     * karşılaştırıyor — olay gövdesine de grubun tamamı yazılıyor.
     */
    onceki: Array<SorguOneriEntity> | null;
    sonraki: Array<SorguOneriEntity> | null;
    onceki_snapshot_id: number | null;
    sonraki_snapshot_id: number | null;
    created_at: string;
}

export interface OneriListeParametreleri {
    durum?: SorguOneriDurum;
    dosya_ref?: string;
    borclu_ref?: string;
    limit?: number;
}

export interface OlayListeParametreleri {
    alan?: string;
    borclu_ref?: string;
    entity_key?: string;
    tip?: SorguOlayTipi;
    limit?: number;
}

// kod inceleme: `listeCevabi`/`temizParametreler` senkronPlanService.ts ile
// birebir kopyaydı; ortak @/services/apiYardimcilari modülüne taşındı.

class SorguOneriService {
    /**
     * `GET /api/v1/sorgu-oneri`
     *
     * `durum` verilmezse uç AÇIK önerileri (yeni + görüldü) döndürür — öneri
     * kutusunun varsayılan listesi budur.
     */
    async listele(parametreler: OneriListeParametreleri = {}): Promise<SorguOneriRow[]> {
        const cevap = await app.$http.get("/api/v1/sorgu-oneri", {
            params: temizParametreler(parametreler)
        });
        return listeCevabi<SorguOneriRow>(cevap);
    }

    /**
     * `PATCH /api/v1/sorgu-oneri/{id}` — durum geçişi.
     *
     * `yok_sayildi` ve `gorev_olusturuldu` kalıcıdır: F2-D4 gereği aynı varlık
     * için kural bir daha tetiklenmez (varlık çıkıp yeniden eklenmedikçe).
     */
    async durumGuncelle(id: number, durum: SorguOneriGecis): Promise<SorguOneriRow> {
        return await app.$http.patch(`/api/v1/sorgu-oneri/${id}`, {durum});
    }

    /**
     * `GET /api/v1/sorgu-olay?dosya_ref=...` — öneri kartının "neden" ayrıntısı.
     *
     * `dosya_ref` uçta zorunlu; boş çağrı 422 döner, bu yüzden burada erkenden
     * boş dizi ile kısa devre yapılır.
     */
    async olaylar(dosyaRef: string, parametreler: OlayListeParametreleri = {}): Promise<SorguOlayRow[]> {
        if (!dosyaRef) return [];
        const cevap = await app.$http.get("/api/v1/sorgu-olay", {
            params: temizParametreler({dosya_ref: dosyaRef, ...parametreler})
        });
        return listeCevabi<SorguOlayRow>(cevap);
    }
}

export default new SorguOneriService();
